import React, { useEffect, useRef, useState } from 'react';
import { Link, Element, scroller } from 'react-scroll';
import AnimatedCursor from 'react-animated-cursor';import Hero from './components/hero/Hero';
import About from './components/about/About';
import ProjectGallery from './components/projects/ProjectGallery';
import Contact from './components/contact/Contact';
import CurveBackground from './components/animation/CurveBackground';
import BlurOverlay from './components/animation/BlurOverlay';
import Header from './components/header/Header';
import Footer from './components/footer/Footer';
import DecryptedText from './components/animation/DecryptedText';
import bg from './assets/background/bg.png';
import logo from './assets/branding/logo.png';
import './App.css';
import Loading from './components/loading/Loading';
import { useGPU, GPUProvider } from './context/GPUContext';
import { initGA, trackPageView } from './utils/Analytics';
import { useViewTimer } from './utils/useViewTimer';
import { Analytics } from '@vercel/analytics/react';


function App() {
  return (
    <GPUProvider>
      <AppContent />
    </GPUProvider>
  );
}

function AppContent() {
  const { hasGPUAcceleration } = useGPU();
  const [showBlur, setShowBlur] = useState(false);
  const [isDetailView, setIsDetailView] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const aboutRef = useRef(null);
  const projectRef = useRef(null);
  const contactRef = useRef(null);

  useViewTimer(aboutRef, 'about');
  useViewTimer(projectRef, 'projects');
  useViewTimer(contactRef, 'contact');

  useEffect(() => {
    initGA();
    trackPageView(window.location.pathname);
  }, []);
  
  const handleScrollToNextSection = () => {
    const sections = ['home', 'about', 'projects', 'contact'];
    for (let i = 0; i < sections.length - 1; i++) {
      const el = document.getElementById(sections[i]);
      if (!el) continue;

      const rect = el.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight / 2) {
        const nextSection = sections[i + 1];
        scroller.scrollTo(nextSection, {
          duration: hasGPUAcceleration ? 600 : 0,
          smooth: hasGPUAcceleration,
        });
        break;
      }
    }
  };

  const handleNavClick = (section) => {
    if (isDetailView) {
      setIsDetailView(false);
      setTimeout(() => {
        scroller.scrollTo(section, {
          duration: hasGPUAcceleration ? 600 : 0,
          smooth: hasGPUAcceleration,
        });
      }, 50);
    } else {
      scroller.scrollTo(section, {
        duration: hasGPUAcceleration ? 600 : 0,
        smooth: hasGPUAcceleration,
      });
    }

    const shouldBlur = section === 'about' || section === 'projects';
    setShowBlur(shouldBlur);
  };

  useEffect(() => {
    document.body.style.overflow = isDetailView ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDetailView]);

  useEffect(() => {
    let aboutVisible = false;
    let projectVisible = false;

    const updateBlur = () => {
      setShowBlur(aboutVisible || projectVisible);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === aboutRef.current) {
            aboutVisible = entry.isIntersecting;
          }
          if (entry.target === projectRef.current) {
            projectVisible = entry.isIntersecting;
          }
        });
        updateBlur();
      },
      { threshold: 0.3 }
    );

    if (aboutRef.current) observer.observe(aboutRef.current);
    if (projectRef.current) observer.observe(projectRef.current);

    return () => {
      if (aboutRef.current) observer.unobserve(aboutRef.current);
      if (projectRef.current) observer.unobserve(projectRef.current);
    };
  }, []);

  return (
    <>
      {hasGPUAcceleration ? (
        <AnimatedCursor
          innerSize={8}
          outerSize={12}
          color="255,255,255"
          outerAlpha={0.2}
          innerScale={0.5}
          outerScale={2.5}
          clickables={[
            'a',
            'li',
            'input',
            'select',
            'textarea',
            'button',
            'label',
            '.back-button',
            '.clickable',
          ]}
        />
      ) : (
        <></>
      )}


      {/* Background */}
      {hasGPUAcceleration ? (
        <CurveBackground />
      ) : (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundImage: `url(${bg})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            zIndex: -1,
          }}
        />
      )}

      <Loading visible={showLoading} onClose={() => setShowLoading(false)} />
      
      <div className="topbar-container">
        <Header />
        <Analytics />
        <div className="navbar">
          <div className="nav-links">
            <Link to="home" smooth={hasGPUAcceleration} duration={hasGPUAcceleration ? 500 : 0} onClick={() => handleNavClick('home')}>
              <DecryptedText text="HOME" speed={50} maxIterations={20} />
            </Link>
            <span className="dash">/</span>
            <Link to="about" smooth={hasGPUAcceleration} duration={hasGPUAcceleration ? 500 : 0} onClick={() => handleNavClick('about')}>
              <DecryptedText text="ABOUT" speed={50} maxIterations={20} />
            </Link>
            <span className="dash">/</span>
            <Link to="projects" smooth={hasGPUAcceleration} duration={hasGPUAcceleration ? 500 : 0} onClick={() => handleNavClick('projects')}>
              <DecryptedText text="PROJECTS" speed={50} maxIterations={20} />
            </Link>
            {/* <span className="dash">/</span>
            <Link to="techstack" smooth={hasGPUAcceleration} duration={hasGPUAcceleration ? 500 : 0} onClick={() => handleNavClick('techstack')}>
              <DecryptedText text="TECH STACK" speed={50} maxIterations={20} />
            </Link> */}
            <span className="dash">/</span>
            <Link to="contact" smooth={hasGPUAcceleration} duration={hasGPUAcceleration ? 500 : 0} onClick={() => handleNavClick('contact')}>
              <DecryptedText text="CONTACT" speed={50} maxIterations={20} />
            </Link>

            <Link to="home" className='logo' smooth={hasGPUAcceleration} duration={hasGPUAcceleration ? 500 : 0} onClick={() => handleNavClick('home')}>
              <div className="nav-logo">
                <img src={logo} alt="logo" />
              </div>
            </Link>
  
          </div>
        </div>
      </div>

      <div className="scroll-container">
        <Element name="home" className="section">
          <Hero />
        </Element>

        <Element name="about" className="section">
          <div ref={aboutRef}>
            <About />
          </div>
        </Element>

        <Element name="projects" className="section">
          <div ref={projectRef}>
            <ProjectGallery
              isDetailView={isDetailView}
              setIsDetailView={(val) => {
                setIsDetailView(val);
                if (val) {
                  setTimeout(() => {
                    scroller.scrollTo('projects', {
                      duration: hasGPUAcceleration ? 600 : 0,
                      smooth: false,
                    });
                  }, 0);
                }
              }}
            />
          </div>
        </Element>

        <Element name="contact" className="section">
          <div ref={contactRef}>
            <Contact />
          </div>
        </Element>
      </div>

      <Footer
        isDetailView={isDetailView}
        onScrollToNext={handleScrollToNextSection}
      />
    </>
  );
}

export default App;
